import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload as UploadIcon, Download, Link2, Unlink, RefreshCcw, CheckCircle2 } from "lucide-react";

export default function InsightsImport() {
  const [history, setHistory] = useState([]);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [ig, setIg] = useState({ connected: false });
  const [form, setForm] = useState({ access_token: "", ig_user_id: "" });

  const load = () => {
    api.get("/import/history").then((r) => setHistory(r.data));
    api.get("/integrations/instagram/status").then((r) => setIg(r.data.username ? { ...r.data, connected: true } : { connected: false }));
  };
  useEffect(() => { load(); }, []);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/import/insights", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.inserted} posts · ${data.duplicates} duplicates · ${data.errors.length} errors`);
      if (data.errors?.length) console.log("Import errors:", data.errors);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };

  const downloadTemplate = () => {
    const token = localStorage.getItem("hop_token");
    const wid = localStorage.getItem("hop_workspace_id");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/import/template?workspace_id=${wid}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((r) => r.blob()).then((b) => {
      const url = URL.createObjectURL(b);
      const a = document.createElement("a");
      a.href = url; a.download = "hop_insights_template.csv"; a.click();
      URL.revokeObjectURL(url);
    });
  };

  const connect = async () => {
    if (!form.access_token || !form.ig_user_id) { toast.error("Both fields required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/integrations/instagram/connect", form);
      toast.success(`Connected @${data.username} · ${data.followers_count?.toLocaleString()} followers`);
      setForm({ access_token: "", ig_user_id: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Connection failed. Check token + IG business account ID.");
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!confirm("Disconnect Instagram? Existing synced data will be kept.")) return;
    await api.delete("/integrations/instagram");
    toast.success("Disconnected");
    load();
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/integrations/instagram/sync");
      toast.success(`Sync complete · ${data.inserted} new · ${data.updated} updated`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); }
    finally { setSyncing(false); }
  };

  return (
    <div className="p-8 max-w-[1200px]">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-1">Instagram Insights</div>
        <h1 className="text-5xl font-black tracking-tight leading-none" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>Import & connect</h1>
        <p className="text-sm text-neutral-600 mt-2">Connect the Instagram Graph API for daily auto-sync, or import CSVs as a fallback.</p>
      </div>

      {/* Meta Graph API Connect */}
      <div className="bg-white border border-neutral-200 rounded-lg p-6 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold flex items-center gap-2"><Link2 className="w-3.5 h-3.5" />Instagram Graph API</div>
          {ig.connected && <div className="flex items-center gap-1 text-xs text-emerald-700 font-semibold"><CheckCircle2 className="w-3.5 h-3.5" />Connected</div>}
        </div>

        {ig.connected ? (
          <div>
            <div className="text-2xl font-black tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>@{ig.username}</div>
            <div className="text-sm text-neutral-600 mt-1">{ig.followers_count?.toLocaleString()} followers · {ig.media_count} posts</div>
            <div className="text-xs text-neutral-500 mt-1">Last sync: {ig.last_synced ? new Date(ig.last_synced).toLocaleString() : "never"}</div>
            <div className="flex gap-2 mt-4">
              <button data-testid="ig-sync-btn" onClick={sync} disabled={syncing}
                className="px-4 py-2 rounded-md font-semibold text-sm text-neutral-900 flex items-center gap-2 disabled:opacity-50" style={{ background: "#D4FF00" }}>
                {syncing ? <RefreshCcw className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
                {syncing ? "Syncing…" : "Sync now"}
              </button>
              <button data-testid="ig-disconnect-btn" onClick={disconnect} className="text-xs font-semibold px-3 py-2 rounded border border-neutral-300 hover:bg-neutral-50 flex items-center gap-1"><Unlink className="w-3 h-3" />Disconnect</button>
            </div>
          </div>
        ) : (
          <div>
            <div className="text-sm text-neutral-600 mb-3">
              You need a <strong>long-lived Page Access Token</strong> and the <strong>Instagram Business Account ID</strong> from your Meta app.
              <a href="https://developers.facebook.com/docs/instagram-platform/instagram-graph-api/get-started" target="_blank" rel="noreferrer" className="text-neutral-900 underline ml-1">How to get these →</a>
            </div>
            <div className="space-y-2">
              <input data-testid="ig-user-id-input" placeholder="Instagram Business Account ID (e.g. 17841400000000000)" value={form.ig_user_id} onChange={(e) => setForm({ ...form, ig_user_id: e.target.value })}
                className="w-full border border-neutral-300 rounded px-3 py-2 text-sm font-mono" />
              <textarea data-testid="ig-token-input" placeholder="Long-lived Access Token (starts with EAA…)" value={form.access_token} onChange={(e) => setForm({ ...form, access_token: e.target.value })}
                rows={3} className="w-full border border-neutral-300 rounded px-3 py-2 text-xs font-mono" />
            </div>
            <button data-testid="ig-connect-btn" onClick={connect} disabled={busy}
              className="mt-3 px-4 py-2 rounded-md font-semibold text-sm text-neutral-900 flex items-center gap-2 disabled:opacity-50" style={{ background: "#D4FF00" }}>
              <Link2 className="w-4 h-4" />{busy ? "Connecting…" : "Connect Instagram"}
            </button>
            <div className="text-xs text-neutral-500 mt-2">Your token is stored server-side and never exposed to the browser after saving.</div>
          </div>
        )}
      </div>

      {/* CSV import */}
      <div className="bg-white border border-neutral-200 rounded-lg p-6 mb-8">
        <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-3">CSV import</div>
        <div className="text-sm text-neutral-600 mb-3">Download the template, fill in your Insights export, and upload. Every column must be present; unknown metrics can be left blank (they'll show as unavailable, not zero).</div>
        <div className="flex flex-wrap items-center gap-3">
          <button data-testid="download-template-btn" onClick={downloadTemplate} className="text-xs font-semibold px-3 py-2 rounded border border-neutral-300 hover:bg-neutral-50 flex items-center gap-1"><Download className="w-3 h-3" />Download template</button>
          <input data-testid="csv-file-input" type="file" accept=".csv" onChange={(e) => setFile(e.target.files[0])} className="text-sm" />
          <button data-testid="upload-btn" onClick={upload} disabled={!file || busy}
            className="px-4 py-2 rounded-md font-semibold text-sm text-neutral-900 flex items-center gap-2 disabled:opacity-50" style={{ background: "#D4FF00" }}>
            <UploadIcon className="w-4 h-4" />{busy ? "Importing..." : "Import CSV"}
          </button>
        </div>
      </div>

      <div className="bg-white border border-neutral-200 rounded-lg p-6">
        <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-3">Import history</div>
        {history.length === 0 && <div className="text-sm text-neutral-500">No imports yet.</div>}
        <div className="space-y-2">
          {history.map((h) => (
            <div key={h.id} className="flex items-center justify-between text-sm border-b border-neutral-100 py-2">
              <div>
                <div className="font-semibold">{h.filename}</div>
                <div className="text-xs text-neutral-500">{h.created_at?.slice(0, 16)}</div>
              </div>
              <div className="text-xs text-neutral-700">Inserted {h.inserted} · Dup {h.duplicates} · Errors {h.errors?.length || 0}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
